import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import { Ticket, Check } from "lucide-react";

const Subscribe = () => {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/create-checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert("Could not start checkout: " + (e?.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  const applyPromo = async () => {
    setPromoError("");
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      await api.post("/billing/apply-promo", { code: promoCode.trim() });
      const u = await refresh();
      setPromoSuccess(true);
      setTimeout(() => {
        if (!u?.onboarding_completed) window.location.replace("/onboarding");
        else if (u?.role === "client" && !u?.linked_practitioner_id) window.location.replace("/link-practitioner");
        else window.location.replace("/dashboard");
      }, 900);
    } catch (e) {
      setPromoError(e?.response?.data?.detail || "Invalid promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12 gap-8 well-page-enter">
      <Logo size="text-4xl" />
      <GlassCard className="w-full max-w-md text-center">
        <p className="text-sm font-medium tracking-wide text-[#8BA38E] uppercase">Activate Subscription</p>
        <p className="mt-3 text-base text-[#4A5D52]">
          Hi {user?.name?.split(" ")[0]}, complete your $4.99/month subscription to start using Well.
        </p>
        <div className="mt-6 flex items-baseline justify-center gap-2">
          <span className="text-lg text-[#8A9C91] line-through">$7.99</span>
          <span className="text-4xl font-bold text-[#2A3B32]">$4.99</span>
          <span className="text-sm text-[#4A5D52]">/month</span>
        </div>
        <p className="mt-1 text-xs text-[#8A9C91]">Limited time — first 6 months</p>

        <div className="mt-8">
          <PrimaryButton onClick={handleSubscribe} disabled={loading || promoSuccess} data-testid="subscribe-btn">
            {loading ? "Redirecting…" : "Continue to Checkout"}
          </PrimaryButton>
        </div>

        {/* Promo code section */}
        <div className="mt-6 pt-6 border-t border-white/40">
          {!promoOpen ? (
            <button
              onClick={() => setPromoOpen(true)}
              data-testid="promo-toggle-btn"
              className="inline-flex items-center gap-2 text-sm text-[#4A5D52] underline underline-offset-4 hover:text-[#2A3B32] transition-colors"
            >
              <Ticket size={14} /> Have a promo code?
            </button>
          ) : (
            <div className="text-left" data-testid="promo-form">
              <label className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Promo code</label>
              <div className="mt-2 flex gap-2">
                <input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  maxLength={20}
                  data-testid="promo-input"
                  className="flex-1 h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] tracking-widest font-semibold uppercase"
                />
                <button
                  onClick={applyPromo}
                  disabled={promoLoading || !promoCode.trim() || promoSuccess}
                  data-testid="promo-apply-btn"
                  className="px-5 h-[48px] bg-[#8BA38E] hover:bg-[#7A917D] text-white rounded-[16px] font-semibold disabled:opacity-50 transition-colors"
                >
                  {promoSuccess ? <Check size={16} /> : promoLoading ? "…" : "Apply"}
                </button>
              </div>
              {promoError && <p className="mt-2 text-sm text-[#B85C5C]" data-testid="promo-error">{promoError}</p>}
              {promoSuccess && (
                <p className="mt-2 text-sm text-[#6A7F6C]" data-testid="promo-success">
                  Promo applied — taking you in…
                </p>
              )}
            </div>
          )}
        </div>

        <button
          onClick={async () => { await logout(); nav("/"); }}
          className="mt-6 text-xs text-[#8A9C91] underline underline-offset-4"
          data-testid="subscribe-signout-btn"
        >
          Sign out
        </button>
      </GlassCard>
    </div>
  );
};

export default Subscribe;
